import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { 
    FaLock, FaMobileAlt, FaArrowLeft, FaWhatsapp, FaKey, 
    FaEye, FaEyeSlash, FaEnvelope, FaShieldAlt, FaSyncAlt 
} from 'react-icons/fa';

const CustomerForgot = () => {
    const [step, setStep] = useState(1);
    const [mobile, setMobile] = useState('');
    const [otpMethod, setOtpMethod] = useState('whatsapp'); // 'whatsapp' or 'email'

    // Step 2 Fields
    const [otp, setOtp] = useState('');
    const [newPin, setNewPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [showPin, setShowPin] = useState(false);

    const [loading, setLoading] = useState(false);
// 🟢 NEW: Resend Timer & WhatsApp Limit States
    const [resendTimer, setResendTimer] = useState(0);
    const [waLimitReached, setWaLimitReached] = useState(false);

    // 🟢 Timer Logic
    useEffect(() => {
        let interval;
        if (resendTimer > 0) interval = setInterval(() => setResendTimer(prev => prev - 1), 1000);
        return () => clearInterval(interval);
    }, [resendTimer]);
    const navigate = useNavigate();

    // Refs for Auto-Focus
    const otpRef = useRef(null);
    const newPinRef = useRef(null);

    const shop = JSON.parse(localStorage.getItem('activeShopData') || '{}');
    const shopId = localStorage.getItem('activeShopId') || shop._id;

    const getFullImageUrl = (img) => {
        if (!img || img === "") return null;
        if (img.startsWith('http') || img.startsWith('data:')) return img;
        let cleanPath = img.replace(/\\/g, "/");
        if (cleanPath.includes('/uploads/')) cleanPath = cleanPath.split('/uploads/')[1];
        else cleanPath = cleanPath.replace('public/', '').replace('uploads/', '');
        if (cleanPath.startsWith('/')) cleanPath = cleanPath.substring(1);
        let BASE_URL = window.location.hostname === 'localhost' ? 'http://localhost:5000' : window.location.origin;
        return `${BASE_URL}/uploads/${cleanPath}`;
    };

    // 🔥 Request OTP with Method Selection
    const handleRequestOTP = async (e) => {
        e.preventDefault();
        if(mobile.length !== 10) return alert("सही 10-अंकों का नंबर डालें।");
        if(!shopId) return alert("स्टोर की लिंक उपलब्ध नहीं है।");

        setLoading(true);
        try {
            const res = await axios.post('/api/customer/forgot-request-otp', { 
                mobile, 
                shopId, 
                otpMethod 
            });
            if(res.data.success) {
                setStep(2);
                setResendTimer(60); // 🟢 60-Second Timer Start
                
                // 🟢 Check if backend blocked WhatsApp due to 24h limit
                if(res.data.message && res.data.message.includes('limit reached')) {
                    setWaLimitReached(true);
                    alert("⚠️ WhatsApp की 24-घंटे की लिमिट पूरी हो गई है! OTP आपके Email पर भेजा गया है।");
                } else {
                    setWaLimitReached(false);
                }
                setTimeout(() => { if(otpRef.current) otpRef.current.focus(); }, 300);
            }
        } catch (err) {
            alert(err.response?.data?.error || "User not found / नंबर नहीं मिला।");
        }
        setLoading(false);
    };

    // 🚀 Reset PIN with 4-Digit Logic
    const handleResetPIN = async (e) => {
        e.preventDefault();
        if(otp.length !== 4) return alert("कृपया सही OTP डालें।");
        if(newPin.length !== 4) return alert("PIN 4 अंकों का होना चाहिए।");
        if(newPin !== confirmPin) return alert("दोनों PIN समान नहीं हैं।");

        setLoading(true);
        try {
            const res = await axios.post('/api/customer/forgot-reset-pin', { 
                mobile, otp, newPin, shopId 
            });

            if(res.data.success) {
                alert("🎉 PIN सफलतापूर्वक बदल दिया गया है!");
                localStorage.setItem('registeredMobile', mobile);
                navigate('/c/login');
            }
        } catch (err) {
            alert(err.response?.data?.error || "Invalid or Expired OTP.");
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-4 font-sans text-white overflow-hidden">
            
            {/* 🛡️ BACKGROUND GLOW */}
            <div className="absolute top-[-5%] left-[-5%] w-72 h-72 bg-orange-600/10 rounded-full blur-3xl"></div>

            <div className="w-full max-w-md bg-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-[40px] shadow-2xl p-8 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-red-500"></div>

                {/* 🏪 BRANDING HEADER */}
                <div className="text-center mb-8">
                    {shop.logo ? (
                        <div className="w-20 h-16 mx-auto mb-4 bg-white rounded-2xl shadow-xl p-1 border border-slate-700 flex items-center justify-center">
                            <img src={getFullImageUrl(shop.logo)} alt="Logo" className="max-w-full max-h-full object-contain"/>
                        </div>
                    ) : (
                        <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-800 flex items-center justify-center text-3xl mb-4 border border-slate-700 text-orange-500 shadow-inner">
                            <FaShieldAlt/>
                        </div>
                    )}
                    <h2 className="text-2xl font-black tracking-tight">
                        {step === 1 ? "Forgot PIN?" : "Verify OTP"}
                    </h2>
                    <p className="text-slate-400 text-xs mt-1 font-medium">
                        Recovery for <span className="text-orange-400 font-bold">{shop.shopName || "Bhavya AI Store"}</span>
                    </p>
                </div>

                {step === 1 ? (
                    <form onSubmit={handleRequestOTP} className="space-y-6 animate-fade-in">
                        <div>
                            <label className="text-[10px] font-black text-slate-500 uppercase ml-2 mb-2 block tracking-widest">Registered Mobile</label>
                            <div className="bg-slate-950/50 border border-slate-700 rounded-2xl flex items-center px-5 py-4 focus-within:border-orange-500 transition-all shadow-inner">
                                <FaMobileAlt className="text-orange-500 mr-3 text-xl shrink-0"/>
                                <input className="flex-1 bg-transparent border-none outline-none text-white font-bold tracking-widest text-lg"
                                    placeholder="10-digit number" value={mobile} onChange={e=>{if(e.target.value.length<=10) setMobile(e.target.value)}} type="number" required />
                            </div>
                        </div>

                        {/* 🛡️ OTP CHOICE BUTTONS */}
                        <div className="bg-slate-800/30 p-3 rounded-2xl border border-slate-700/50">
                            <p className="text-[9px] font-black text-slate-500 uppercase text-center mb-3 tracking-[0.2em]">Send Code Via</p>
                            <div className="grid grid-cols-2 gap-3">
                                <button type="button" onClick={()=>setOtpMethod('whatsapp')} 
                                    className={`flex items-center justify-center gap-2 py-3 rounded-xl text-[11px] font-black transition-all ${otpMethod === 'whatsapp' ? 'bg-green-600 shadow-lg scale-105' : 'bg-slate-800 opacity-50'}`}>
                                    <FaWhatsapp size={14}/> WHATSAPP
                                </button>
                                <button type="button" onClick={()=>setOtpMethod('email')} 
                                    className={`flex items-center justify-center gap-2 py-3 rounded-xl text-[11px] font-black transition-all ${otpMethod === 'email' ? 'bg-blue-600 shadow-lg scale-105' : 'bg-slate-800 opacity-50'}`}>
                                    <FaEnvelope size={14}/> EMAIL ID
                                </button>
                            </div>
                        </div>

                        <button disabled={loading} className="w-full bg-gradient-to-r from-orange-600 to-red-600 py-5 rounded-[24px] font-black text-lg shadow-xl hover:scale-[1.02] active:scale-95 transition-all">
                            {loading ? "SENDING..." : "GET SECURITY CODE"}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleResetPIN} className="space-y-6 animate-fade-in">
                        <div className="bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-2xl text-center">
                            <p className="text-xs text-emerald-400 font-bold tracking-wide flex items-center justify-center gap-2">
                                <FaShieldAlt/> Code sent to your {otpMethod}
                            </p>
                        </div>

                        <div>
                            <label className="text-[10px] font-black text-slate-500 uppercase text-center block mb-2 tracking-widest">Enter 4-Digit OTP</label>
                            <input ref={otpRef} className="bg-slate-950/50 border border-slate-700 rounded-3xl w-full py-5 text-4xl text-center font-black tracking-[1em] text-orange-400 outline-none focus:border-orange-500 shadow-inner"
                                placeholder="••••" value={otp} onChange={e=>{if(e.target.value.length<=4) setOtp(e.target.value)}} type="number" required />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-[10px] font-black text-slate-500 uppercase ml-2 mb-2 block tracking-widest">New PIN</label>
                                <input ref={newPinRef} className="bg-slate-950/50 border border-slate-700 rounded-2xl w-full py-3.5 text-center font-black tracking-widest text-xl outline-none focus:border-blue-500"
                                    placeholder="••••" value={newPin} onChange={e=>{if(e.target.value.length<=4) setNewPin(e.target.value)}} type={showPin ? "text" : "password"} required />
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-slate-500 uppercase ml-2 mb-2 block tracking-widest">Confirm</label>
                                <input className="bg-slate-950/50 border border-slate-700 rounded-2xl w-full py-3.5 text-center font-black tracking-widest text-xl outline-none focus:border-blue-500"
                                    placeholder="••••" value={confirmPin} onChange={e=>{if(e.target.value.length<=4) setConfirmPin(e.target.value)}} type={showPin ? "text" : "password"} required />
                            </div>
                        </div>

                        <div className="flex justify-center">
                            <button type="button" onClick={() => setShowPin(!showPin)} className="text-[10px] font-black text-slate-400 hover:text-white flex items-center gap-2 bg-slate-800 px-4 py-2 rounded-full border border-slate-700 transition-all">
                                {showPin ? <FaEyeSlash/> : <FaEye/>} {showPin ? "HIDE" : "SHOW"} PINS
                            </button>
                        </div>

                        <button disabled={loading} className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 py-5 rounded-[24px] font-black text-lg shadow-xl hover:scale-[1.02] active:scale-95 transition-all">
                            {loading ? "UPDATING..." : "RESET & SECURE PIN"}
                        </button>
{/* 🟢 RESEND OTP BUTTON */}
                        <div className="text-center mt-4">
                            <button
                                type="button"
                                disabled={resendTimer > 0 || loading}
                                onClick={handleRequestOTP}
                                className={`text-[10px] font-black uppercase tracking-widest transition-all ${
                                    resendTimer > 0 ? 'text-slate-600 cursor-not-allowed' : 'text-orange-500 hover:text-orange-400 hover:underline'
                                }`}
                            >
                                {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "🔄 Resend OTP Now"}
                            </button>
                            
                            {waLimitReached && (
                                <p className="text-[9px] text-red-500 mt-2 font-bold uppercase tracking-widest animate-pulse">
                                    ⚠️ WhatsApp Locked (24h). OTP sent via Email.
                                </p>
                            )}
                        </div>
                      </form>
                )}

                <div className="mt-8 pt-6 border-t border-slate-800 text-center">
                    <button onClick={() => step === 1 ? navigate('/c/login') : setStep(1)} className="text-slate-500 hover:text-white text-xs font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition-all">
                        <FaArrowLeft/> Back to Login
                    </button>
                </div>
            </div>

            {/* 🌟 PREMIUM FOOTER */}
            <div className="mt-12 text-center opacity-60">
                <p className="text-[10px] font-bold text-slate-500 tracking-[0.5em] uppercase">Security Engine</p>
                <h1 className="text-xl font-black bg-clip-text text-transparent bg-gradient-to-r from-orange-400 via-red-400 to-orange-500">BHAVYA AI™</h1>
            </div>
        </div>
    );
};

export default CustomerForgot;